import { useEffect, useState } from 'react';

import getInitialFilter from '../utils/getInitialFilter';
import {
  tableFiltersToFilterValues,
  TableFilterOptions
} from '../utils/tableFilters';
import { useQueryParams, useDebounce } from '../hooks';

export type UseParamsOptions<T extends Record<string, unknown>> = TableFilterOptions<T> & {
  normalizeFilter?: (filter: T) => T;
};

/**
 * Хук для работы с пагинацией и фильтрами
 */

function useParams<T extends Record<string, unknown>>(
  initialFilter: T,
  callback: (params: T & { pageIndex: number }) => void,
  sync = true,
  options?: UseParamsOptions<T>
) {
  const { queryParams, setQueryParams } = useQueryParams();
  const [pageIndex, setPageIndex] = useState(Number(queryParams.get('pageIndex')) || 0);
  const canonicalize = (next: T): T =>
    options?.normalizeFilter ? options.normalizeFilter(next) : next;
  const [filter, setFilter] = useState(() => canonicalize(getInitialFilter<T>(initialFilter)));
  const debounce = useDebounce(filter, 500);

  useEffect(() => {
    if (sync) {
      const params = { ...filter, pageIndex };

      callback(params);

      setQueryParams(params);
    }
  }, [debounce]);

  // tslint:disable-next-line: no-shadowed-variable
  const handlePageChange = (pageIndex: number) => {
    setPageIndex(pageIndex);

    const params = { ...filter, pageIndex };

    callback(params);

    setQueryParams(params);
  };

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;

    // tslint:disable-next-line: no-shadowed-variable
    setFilter(filter => canonicalize({ ...filter, [name]: value }));

    setPageIndex(0);
  };

  const handleTableFilterChange = (event: Record<string, unknown>) => {
    const output = tableFiltersToFilterValues<T>(event, options);

    // tslint:disable-next-line: no-shadowed-variable
    setFilter(prevFilter => canonicalize({ ...prevFilter, ...output }));

    setPageIndex(0);
  };

  const handleFilterApply = () => {
    const params = { ...filter, pageIndex };

    callback(params);

    setQueryParams(params);
  };

  const handleFilterReset = (resetQuery?: boolean) => {
    setFilter(canonicalize(initialFilter));

    setPageIndex(0);

    if (resetQuery) {
      setQueryParams({});
    }
  };

  return {
    pageIndex,
    filter,
    handlePageChange,
    handleFilterChange,
    handleFilterApply,
    handleFilterReset,
    handleTableFilterChange
  };
}

export default useParams;
